import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchEmpreendimentos, fetchUnidades, clearEmpreendimentosCache } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { useAlertHelpers } from '../components/layout/Alert/AlertHelpers';

export const usePropertySelection = (isMRV) => {
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [propertyDetails, setPropertyDetails] = useState({
    value: '',
    priceTableVersion: '',
    address: ''
  });

  // Cache de dados
  const [allEmpreendimentos, setAllEmpreendimentos] = useState([]);
  const [unidadesCache, setUnidadesCache] = useState({});

  // Ref para evitar chamadas duplicadas
  const hasLoadedCities = useRef(false);
  const loadingCities = useRef(false);
  const loadingUnits = useRef(false);
  const isSettingCity = useRef(false);
  const isSettingProperty = useRef(false);
  const isSettingUnit = useRef(false);

  // Garantir que isLoading seja sempre um booleano
  const isLoading = loading === true;

  // Função auxiliar para converter textos para maiúsculas
  const toUpperCase = (text) => (text || '').toUpperCase();

  // Mensagens de alerta
  const { showError } = useAlertHelpers();

  // Carregar cidades ao inicializar
  useEffect(() => {
    // Resetar ref ao mudar marca para forçar recarregamento
    hasLoadedCities.current = false;
    loadingCities.current = false;
    
    // Verificar se já carregamos as cidades (evita duplicação)
    if (hasLoadedCities.current || loadingCities.current) return;

    const loadCities = async () => {
      setLoading(true);
      loadingCities.current = true;

      try {
        console.log(`Iniciando carregamento de cidades para ${isMRV ? 'MRV' : 'Sensia'}...`);
        // Marca como carregado antes da chamada para evitar duplicações
        hasLoadedCities.current = true;

        // Limpar cache da API para forçar nova requisição
        clearEmpreendimentosCache(isMRV, 'Carteira');
        
        // Limpar cache local de unidades
        setUnidadesCache({});

        // Pass isMRV parameter to the fetch function
        const data = await fetchEmpreendimentos(isMRV, 'Carteira');
        console.log('Cidades carregadas com sucesso');
        const uniqueData = [...new Map(data.map(emp => [emp.id, emp])).values()];
        setAllEmpreendimentos(uniqueData);

        // Extrair cidades únicas
        const cidadesMap = {};
        uniqueData.forEach(emp => {
          if (emp.cidade && !cidadesMap[emp.cidade.id]) {
            cidadesMap[emp.cidade.id] = emp.cidade;
          }
        });

        const cidadesUnicas = Object.values(cidadesMap);
        const cidadesOrdenadas = [...cidadesUnicas]
          .sort((a, b) => a.nome.toLowerCase().localeCompare(b.nome.toLowerCase()))
          .map(cidade => ({
            value: cidade.id,
            label: toUpperCase(cidade.nome + ' - ' + cidade.unidadeFederativa),
            state: cidade.unidadeFederativa
          }));

        setCities(cidadesOrdenadas);

        // Reset selected values when company changes
        setSelectedCity(null);
        setSelectedProperty('');

        // Selecionar automaticamente a primeira cidade
        if (cidadesOrdenadas.length > 0) {
          isSettingCity.current = true;
          setSelectedCity(cidadesOrdenadas[0].value);
        }
      } catch (error) {
        console.error('Erro ao obter informações da cidade:', error);
        showError('Erro ao obter informações da cidade!', { title: 'Erro', duration: 4000 });
        hasLoadedCities.current = false;
      } finally {
        loadingCities.current = false;
        setLoading(false);
        isSettingCity.current = false;
      }
    };

    loadCities();
  }, [isMRV]);

  // Filtrar empreendimentos quando cidade muda
  useEffect(() => {
    if (!selectedCity) {
      setProperties([]);
      setSelectedProperty('');
      return;
    }

    // Evitar processamento durante seleção automática inicial
    if (isSettingCity.current) return;

    setLoading(true);
    if (allEmpreendimentos.length) {

      const filteredEmpreendimentos = allEmpreendimentos
        .filter(emp => emp.cidade && emp.cidade.id === selectedCity);

      const empreendimentosFiltrados = [...filteredEmpreendimentos]
        .sort((a, b) => a.nome.toLowerCase().localeCompare(b.nome.toLowerCase()))
        .map(emp => ({
          value: emp.id,
          label: toUpperCase(emp.nome)
        }));

      
      setProperties(empreendimentosFiltrados);

      // Selecionar automaticamente o primeiro empreendimento
      if (empreendimentosFiltrados.length > 0) {
        isSettingProperty.current = true;
        setSelectedProperty(empreendimentosFiltrados[0].value);
      } else {
        // Se não houver empreendimentos, limpar seleção
        setSelectedProperty('');
      }
    } else {
      setProperties([]);
      setSelectedProperty('');
    }

    // Reset valores dependentes
    setUnits([]);
    setSelectedUnit('');
    setPropertyDetails({
      value: '',
      priceTableVersion: '',
      address: ''
    });

    setLoading(false);
    isSettingProperty.current = false;
  }, [selectedCity, allEmpreendimentos]);

  // Carregar unidades quando empreendimento muda
  useEffect(() => {
    if (!selectedProperty || loadingUnits.current) {
      return;
    }

    // Evitar processamento durante seleção automática
    if (isSettingProperty.current) return;

    const loadUnits = async () => {
      setLoading(true);
      loadingUnits.current = true;

      try {
        let unidadesData;
        if (unidadesCache[selectedProperty]) {
          console.log('Usando cache de unidades');
          unidadesData = unidadesCache[selectedProperty];
        } else {
          console.log('Buscando unidades na API');
          unidadesData = await fetchUnidades(selectedProperty, isMRV, 'Carteira');
          // Guardar em cache
          setUnidadesCache(prev => ({
            ...prev,
            [selectedProperty]: unidadesData
          }));
        }

        if (!unidadesData || unidadesData.length === 0) {
          // Se não houver unidades, adicionamos uma opção indicando isso
          setUnits([{ value: '', label: 'NÃO POSSUI UNIDADES', disabled: true }]);
          setSelectedUnit('');

          // Limpar os campos de detalhes quando não houver unidades
          setPropertyDetails({
            value: '',
            priceTableVersion: '',
            address: ''
          });
        } else {          
          const sortedUnidades = [...unidadesData].sort((a, b) => {
            const nomeA = (a.nome || `Unidade ${a.numero}`).toLowerCase();
            const nomeB = (b.nome || `Unidade ${b.numero}`).toLowerCase();
            return nomeA.localeCompare(nomeB);
          });

          const unidadesFormatadas = sortedUnidades.map(unidade => ({
            value: unidade.id,
            label: toUpperCase(unidade.nome || `Unidade ${unidade.numero}`), 
            data: unidade 
          }));

          setUnits(unidadesFormatadas);

          // Selecionar automaticamente a primeira unidade
          if (unidadesFormatadas.length > 0) {
            isSettingUnit.current = true;
            setSelectedUnit(unidadesFormatadas[0].value);
          } else {
            setSelectedUnit('');
            // Limpar os campos de detalhes se não houver unidades formatadas
            setPropertyDetails({
              value: '',
              priceTableVersion: '',
              address: ''
            });
          }
        }
      } catch (error) {
        console.error('Erro ao obter informações da unidade:', error);
        showError('Erro ao obter informações da unidade!', { title: 'Erro', duration: 4000 });
        setUnits([{ value: '', label: 'ERRO AO CARREGAR UNIDADES', disabled: true }]);
        setSelectedUnit('');
        setPropertyDetails({ value: '', priceTableVersion: '', address: '' });
      } finally {
        loadingUnits.current = false;
        setLoading(false);
        isSettingUnit.current = false;
      }
    };

    loadUnits();
  }, [selectedProperty]);

  // Atualizar detalhes quando unidade muda
  useEffect(() => {
    if (!selectedUnit || units.length === 0) return;

    // Evitar processamento durante seleção automática
    if (isSettingUnit.current) return;

    const selectedUnitData = units.find(unit => unit.value === selectedUnit)?.data;
    if (!selectedUnitData) return;

    // Extrair detalhes da unidade
    const valor = selectedUnitData.unidadePreco?.valorImovelComDelta;
    const versao = selectedUnitData.unidadePreco?.versaoTabelaPrecos || '';

    // Obter nomes para construir endereço em MAIÚSCULO
    const cityName = toUpperCase(cities.find(c => c.value === selectedCity)?.label || '');
    const propertyName = toUpperCase(properties.find(p => p.value === selectedProperty)?.label || '');
    const unitName = toUpperCase(selectedUnitData.nome || `Unidade ${selectedUnitData.numero}`);

    setPropertyDetails({
      value: valor ? formatCurrency(valor) : '',
      priceTableVersion: versao,
      address: `${propertyName} - ${unitName}, ${cityName}`
    });
  }, [selectedUnit, units, selectedCity, selectedProperty, cities, properties]);

  // Handlers para mudanças nos selects - memoizados para evitar recriações
  const handleCityChange = useCallback((e) => {
    setSelectedCity(e.target.value);
  }, []);

  const handlePropertyChange = useCallback((e) => {
    setSelectedProperty(e.target.value);
  }, []);

  const handleUnitChange = useCallback((e) => {
    setSelectedUnit(e.target.value);
  }, []);

  // Obter opções para os selects com mensagens apropriadas
  const getPropertyOptions = useCallback(() => {
    if (properties.length === 0 && selectedCity) {
      return [{ value: '', label: 'NÃO HÁ EMPREENDIMENTOS DISPONÍVEIS', disabled: true }];
    } else if (properties.length === 0) {
      return [{ value: '', label: 'SELECIONE UMA CIDADE PRIMEIRO', disabled: true }];
    }

    return properties;
  }, [properties, selectedCity]);

  const getUnitOptions = useCallback(() => {
    if (!selectedProperty) {
      return [{ value: '', label: 'SELECIONE UM EMPREENDIMENTO', disabled: true }];
    }

    if (loading && units.length === 0) {
      return [{ value: '', label: 'CARREGANDO UNIDADES...', disabled: true }];
    }

    // Se a primeira unidade já tem uma mensagem "Não possui unidades", retornamos as unidades como está
    if (units.length === 1 && units[0].disabled) {
      return units;
    }

    return units;
  }, [selectedProperty, loading, units]);

  return {
    isLoading,
    cities,
    properties: getPropertyOptions(),
    units: getUnitOptions(),
    selectedCity,
    selectedProperty,
    selectedUnit,
    propertyValue: propertyDetails.value,
    priceTableVersion: propertyDetails.priceTableVersion,
    address: propertyDetails.address,
    handleCityChange,
    handlePropertyChange,
    handleUnitChange
  };
};